import React, { useState } from "react"
import TextButton from "../../atoms/buttons/TextButton"
import { DownloadIcon } from "../../atoms/icons/DownloadIcon"
import { AnimatePresence } from "framer-motion"
import Modal from "../Modals/Modal"
import { createPortal } from "react-dom"
import { useSendProductSheet } from "../../../hooks/useSendProductSheet"
import { SendProductSheetEmailInput } from "../../../graphql/mutations/sendProductSheet"
import { FormModalContent } from "./FormModalContent"
import { SendProductSheetForm } from "./forms/SendProductSheetForm"
import {
  pushFormSubmitEvent,
  FORM_NAME_DOWNLOAD_KU,
  FORM_NAME_DOWNLOAD_SECURE,
  FORM_NAME_DOWNLOAD_RENTAL,
  FORM_NAME_DOWNLOAD_MOTION,
} from "../../../utils/datalayer"
import SmallColorfulButton from "../../atoms/buttons/SmallColorfulButton"
import { ApolloError } from "@apollo/client"
import { captureExceptionWithContext } from "../../../utils/sentry"

const productSheetTitles = {
  satcube_ku: "Satcube Ku",
  satcube_rental: "Satcube Instant",
  satcube_motion: "Satcube Motion",
  satcube_secure: "Satcube Secure",
}

type ProductSheetType = keyof typeof productSheetTitles

type Props = {
  productSheetType: ProductSheetType
  buttonType?: "text" | "colorBlue" | "colorgreen" | "coloryellow" | "colorred"
}
interface productSheetErrors {
  field: string
  messages: string
}

export const DownloadProductSheetButton: React.FC<Props> = ({ buttonType = "text", ...props }) => {
  const descriptionPrefix =
    props.productSheetType === "satcube_ku"
      ? "portable and lightweight "
      : props.productSheetType === "satcube_secure"
        ? "portable and robust "
        : ""
  const descriptionSuffix =
    props.productSheetType === "satcube_motion"
      ? " for on-the-move communications"
      : props.productSheetType === "satcube_rental"
        ? " rental service"
        : ""
  const formTitle = "Download product sheet"
  const formDescription1 = `
    At Satcube, our mission is to simplify access to satellite broadband by reducing the complexity and
    making our products easy to use
    ${
      props.productSheetType === "satcube_secure" || props.productSheetType === "satcube_rental"
        ? ", even in difficult conditions."
        : "."
    }
    This is why we have created the
    ${descriptionPrefix}${productSheetTitles[props.productSheetType]}${descriptionSuffix}.
    ${
      props.productSheetType === "satcube_rental"
        ? ""
        : "The product sheet contains technical specifications to provide a deeper understanding of the terminal."
    }
  `
  const formDescription2 =
    props.productSheetType === "satcube_rental"
      ? `This service sheet contains all the practical details and prices. 
         Simply fill out your email address to receive it in your inbox.`
      : `
    Simply fill out the form to learn more about the unique ${productSheetTitles[props.productSheetType]}.
  `
  const [isOpen, setIsOpen] = useState(false)
  const [isSent, setIsSent] = useState(false)
  const [errors, setErrors] = useState<productSheetErrors[]>([])
  const [link, setLink] = useState<string>()

  const onSendProductSheetCompleted = (data: any) => {
    if (data?.sendProductSheet?.errors?.length > 0) {
      let errorMessages: productSheetErrors[] = []
      data.sendProductSheet.errors.map((e: any) => {
        errorMessages.push({ field: e.field, messages: e.messages })
      })
      setErrors(errorMessages)
    } else {
      setLink(data?.sendProductSheet?.link)
      setIsSent(true)
      setErrors([])
      pushFormSubmitEvent(
        props.productSheetType === "satcube_ku"
          ? FORM_NAME_DOWNLOAD_KU
          : props.productSheetType === "satcube_secure"
            ? FORM_NAME_DOWNLOAD_SECURE
            : props.productSheetType === "satcube_rental"
              ? FORM_NAME_DOWNLOAD_RENTAL
              : props.productSheetType === "satcube_motion"
                ? FORM_NAME_DOWNLOAD_MOTION
                : "",
      )
    }
  }

  const onSendProductSheetError = (error: ApolloError) => {
    captureExceptionWithContext(error, "error sending product sheet")
    setIsSent(false)
  }

  const { sendProductSheet, data } = useSendProductSheet({
    onCompleted: (data: any) => onSendProductSheetCompleted(data),
    onError: onSendProductSheetError,
  })

  const handleSubmit = (input: SendProductSheetEmailInput): Promise<void> => {
    return new Promise((resolve, reject) => {
      sendProductSheet({
        variables: {
          input: input,
        },
      })
      resolve()
    })
  }

  const Button: React.FC = () => {
    switch (buttonType) {
      case "text":
        return <TextButton title="Product sheet" icon={<DownloadIcon />} onClick={() => setIsOpen(true)} />
      case "colorBlue":
        return (
          <SmallColorfulButton
            title="Product sheet"
            icon={<DownloadIcon color="white" />}
            onClick={() => setIsOpen(true)}
          />
        )
      case "colorgreen":
        return (
          <SmallColorfulButton
            title="Product sheet"
            icon={<DownloadIcon color="white" />}
            onClick={() => setIsOpen(true)}
            variant="green"
          />
        )
      case "coloryellow":
        return (
          <SmallColorfulButton
            title="Service specification"
            icon={<DownloadIcon />}
            onClick={() => setIsOpen(true)}
            variant="yellow"
          />
        )
      case "colorred":
        return (
          <SmallColorfulButton
            title="Product sheet"
            icon={<DownloadIcon color="white" />}
            onClick={() => setIsOpen(true)}
            variant="red"
          />
        )
      default:
        return <TextButton title="Product sheet" icon={<DownloadIcon />} onClick={() => setIsOpen(true)} />
    }
  }

  return (
    <>
      <Button />
      {isOpen &&
        createPortal(
          <AnimatePresence mode="wait">
            <Modal open={isOpen} onClose={() => setIsOpen(false)} closeIcon>
              <FormModalContent
                formTitle={formTitle}
                formDescription1={formDescription1}
                formDescription2={formDescription2}
              >
                <SendProductSheetForm
                  handleSubmit={handleSubmit}
                  productSheetType={props.productSheetType}
                  isSent={isSent}
                  setIsSent={setIsSent}
                  errors={errors}
                  link={link}
                />
              </FormModalContent>
            </Modal>
          </AnimatePresence>,
          document.body,
        )}
    </>
  )
}
